import { CheckCircledIcon } from "@radix-ui/react-icons"
import { Facet, facetGroups } from "data/facets"
import { LevelName } from "app/constants"
import { facetsAtom } from "app/state"
import { BaseMapStyle, MUTED_MAP_STYLE, SATELLITE_MAP_STYLE } from "hooks/use_base_map"
import { useAtomValue } from "jotai"
import React, { useCallback, useState, type Dispatch, type SetStateAction } from "react"
import { LegendBackHeader } from "./legend-back-header"
import { FacetTooltip } from "./facet-tooltip"
import { FacetName } from "./facet-name"
import { City } from "@prisma/client"
import { useTranslation } from "react-i18next"
import mapboxgl from "mapbox-gl"
import Help from "components/help"
import { toggleLayer } from "utils"
import { BASE_LAYERS, type BaseLayerConfig } from "app/features/regional-map/regional-map.constants"

function FacetOption({
  facet,
  active,
  onClick,
  city,
}: {
  facet: Facet
  active: boolean
  onClick: () => void
  city?: City
}) {
  return (
    <button
      key={facet?.field?.field}
      className={`group flex cursor-pointer items-center justify-between gap-x-1 rounded-full px-1 py-0.5 text-sm transition-all ${
        active
          ? "bg-brand-green-dark text-white"
          : "text-gray-700 hover:bg-brand-green hover:text-white"
      }`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <CheckCircledIcon
          className={`mr-2 inline text-white transition-all group-hover:text-brand-green`}
        />
        <FacetName facetKey={facet?.field?.field} city={city} />
      </div>
      <FacetTooltip facetKey={facet?.field?.field} active={active} city={city} />
    </button>
  )
}

const FacetWithSublayersOption = ({
  facet,
  active,
  onClick,
  activeFacet,
  city,
}: {
  facet: Facet
  active: boolean
  onClick: (facet: Facet) => void
  activeFacet: Facet | null
  city?: City
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const handleShowSublayers = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  return (
    <div className="relative">
      <button
        className={`group flex w-full cursor-pointer items-center justify-between gap-x-1 rounded-t-md px-1 py-0.5 text-sm transition-all ${
          active ? "text-gray-700" : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
        }`}
        onClick={handleShowSublayers}
      >
        <div className="flex items-center">
          <svg
            className="mr-2 h-4 w-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={active ? "M19 9l-7 7-7-7" : "M9 5l7 7-7 7"}
            />
          </svg>
          <FacetName facetKey={facet.title} city={city} />
        </div>

        <FacetTooltip facetKey={facet.title} city={city} hasSublayers={true} />
      </button>

      {isOpen && (
        <div className="w-full bg-white">
          {facet.sublayers?.map((sublayer: Facet) => {
            const isSelected = activeFacet?.field.field === sublayer.field.field
            return (
              <button
                key={sublayer.field.field}
                className={`mb-1 flex w-[95%] cursor-pointer items-center justify-self-end rounded-full px-1 py-0.5 text-sm transition-all ${
                  isSelected
                    ? "rounded-full bg-brand-green-dark text-white"
                    : "text-gray-700 hover:bg-brand-green hover:text-white"
                }`}
                onClick={() => {
                  onClick(sublayer)
                }}
              >
                <CheckCircledIcon
                  className={`${
                    isSelected ? "text-white" : "opacity-0"
                  } mr-2 inline transition-all`}
                />
                <FacetName facetKey={sublayer.field.field} city={city} />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function BaseMapOption(props: {
  title: string
  style: BaseMapStyle
  setBaseMapStyle: Dispatch<SetStateAction<BaseMapStyle>>
  currentBaseMapStyle: BaseMapStyle
}) {
  const { title, style, setBaseMapStyle, currentBaseMapStyle } = props
  return (
    <>
      <button
        className={`group flex cursor-pointer items-center gap-x-1 rounded-full px-1 py-0.5 text-sm transition-all ${
          style === currentBaseMapStyle
            ? "bg-brand-green-dark text-white"
            : "text-gray-500 hover:bg-brand-green hover:text-white"
        }`}
        onClick={() => setBaseMapStyle(style)}
      >
        <svg className={`h-4 w-4 text-white`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {title}
      </button>
    </>
  )
}

function LayerToggleButton({
  layer,
  active,
  onClick,
}: {
  layer: BaseLayerConfig
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      key={layer.id}
      className={`group flex cursor-pointer items-center justify-between gap-x-1 rounded-full px-1 py-0.5 text-sm transition-all ${
        active
          ? "bg-brand-green-dark text-white"
          : "text-gray-700 hover:bg-brand-green hover:text-white"
      }`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <CheckCircledIcon
          className={`mr-2 inline text-white transition-all group-hover:text-brand-green`}
        />
        <span>{layer.title}</span>
      </div>
      <Help className={`group-hover:text-white ${active ? "text-white" : "text-brand-green"}`}>
        <>
          <p className="text-left">{layer.explanation}</p>
          <p className="pt-2 text-left text-xs text-gray-400">
            {`[Data Source: ${layer.dataSource}]`}
          </p>
        </>
      </Help>
    </button>
  )
}

export const FacetsPanel = ({
  activeFacet,
  currentLevel,
  baseMapStyle,
  onModeChange,
  setActiveFacet,
  setBaseMapStyle,
  city,
  cityId,
  map,
}: {
  activeFacet: Facet
  currentLevel: LevelName
  baseMapStyle: BaseMapStyle
  setActiveFacet: React.Dispatch<React.SetStateAction<Facet | null>>
  setBaseMapStyle: React.Dispatch<React.SetStateAction<BaseMapStyle>>
  onModeChange: React.Dispatch<React.SetStateAction<"facets" | "filters" | null>>
  city?: City
  cityId?: string
  map?: mapboxgl.Map | null
}) => {
  const facets = useAtomValue(facetsAtom)
  const { t } = useTranslation(["map"])
  const [activeBaseLayer, setActiveBaseLayer] = useState<string | null>(null)
  const baseLayers = cityId ? BASE_LAYERS[cityId] : undefined

  const handleLayerToggle = useCallback(
    (layerId: string, layerOpacity: number) => {
      if (map) {
        if (layerId === activeBaseLayer) {
          // Clicking the already-active layer -> turn it off
          toggleLayer({ map, layerId, layerOpacity })
          setActiveBaseLayer(null)
        } else if (activeBaseLayer) {
          // A different layer is active -> turn on new, turn off old
          toggleLayer({ map, layerId, layerOpacity })
          toggleLayer({ map, layerId: activeBaseLayer, layerOpacity })
          setActiveBaseLayer(layerId)
        } else {
          // No layer active -> turn on clicked
          toggleLayer({ map, layerId, layerOpacity })
          setActiveBaseLayer(layerId)
        }
      }
    },
    [map, activeBaseLayer]
  )

  return (
    <div className="w-full pb-3">
      <LegendBackHeader title={t("common:layers")} onClick={() => onModeChange(null)} />
      <div className="space-y-4 p-3">
        {facetGroups.map((facetGroup) => {
          if (facetGroup.title === "Hide") return null
          return (
            <div key={facetGroup.title}>
              {facets.filter(
                (facet) =>
                  facet.group === facetGroup &&
                  facet.field?.type === (currentLevel == "Parcel" ? "Area" : currentLevel)
              ).length > 0 && (
                <div className="pb-1 text-xs font-semibold uppercase text-gray-600">
                  {t(`${facetGroup.title}`)}
                </div>
              )}
              <div className="flex flex-col items-stretch space-y-1">
                {facets
                  .filter(
                    (facet) =>
                      facet.group?.title === facetGroup.title &&
                      facet.field?.type === (currentLevel == "Parcel" ? "Area" : currentLevel)
                  )
                  .map((facet) => {
                    const active = facet.field?.field === activeFacet?.field.field
                    return facet.sublayers && facet.sublayers.length > 0 ? (
                      <FacetWithSublayersOption
                        key={facet.field.field}
                        facet={facet as Facet}
                        active={active}
                        activeFacet={activeFacet}
                        city={city}
                        onClick={(facet: Facet) => {
                          if (active) setActiveFacet(null)
                          else setActiveFacet(facet)
                        }}
                      />
                    ) : (
                      <FacetOption
                        key={facet.field.field}
                        facet={facet as Facet}
                        active={active}
                        city={city}
                        onClick={() => {
                          if (active) setActiveFacet(null)
                          else setActiveFacet(facet as Facet)
                        }}
                      />
                    )
                  })}
              </div>
            </div>
          )
        })}
        {baseLayers && baseLayers.length > 0 && (
          <div>
            <div className="pb-1 text-xs font-semibold uppercase text-gray-600">
              {t("map:legend.base_layers", "Base layers")}
            </div>
            <div className="flex flex-col items-stretch space-y-1">
              {baseLayers.map((layer) => (
                <LayerToggleButton
                  key={layer.id}
                  layer={layer}
                  onClick={() => handleLayerToggle(layer.id, layer.opacity)}
                  active={layer.id === activeBaseLayer}
                />
              ))}
            </div>
          </div>
        )}
        <div>
          <div className="pb-1 text-xs font-semibold uppercase text-gray-600">
            {t("map:legend.base_map")}
          </div>
          <div className="flex flex-col items-stretch space-y-1">
            <BaseMapOption
              title={t("map:legend.default")}
              style={MUTED_MAP_STYLE}
              setBaseMapStyle={setBaseMapStyle}
              currentBaseMapStyle={baseMapStyle}
            />
            <BaseMapOption
              title={t("map:legend.satellite")}
              style={SATELLITE_MAP_STYLE}
              setBaseMapStyle={setBaseMapStyle}
              currentBaseMapStyle={baseMapStyle}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
